/* global SideDrawer */

if (!customElements.get('pharmacist-form-drawer')) {
  class PharmacistFormDrawer extends SideDrawer {
    constructor() {
      super();
      this.form = this.querySelector('.js-pharmacist-form');
      this.submitBtn = this.querySelector('.js-pharmacist-submit');
      this.errorEl = this.querySelector('.js-pharmacist-form-error');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.productFormId = this.dataset.productFormId;

      this.form.addEventListener('submit', this.handleSubmit.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));

      document.addEventListener('click', this.handleDocumentClick.bind(this));
      document.addEventListener('change', this.handlePdpDoseChange.bind(this));

      this.syncPdpDoseToMainForm();
    }

    /**
     * Main PDP product form (source of truth for variant/qty after dose moved to PDP).
     * @returns {HTMLFormElement|null}
     */
    getMainForm() {
      if (this.mainForm) return this.mainForm;
      return document.getElementById(this.productFormId)
        || document.querySelector('.js-product-form-main');
    }

    /**
     * PDP dose select (if present).
     * @returns {HTMLSelectElement|HTMLInputElement|null}
     */
    getPdpVariantEl() {
      return document.querySelector('.js-pharmacist-pdp-variant');
    }

    /**
     * Sync PDP dose select + qty into the main product form id/quantity fields.
     */
    syncPdpDoseToMainForm() {
      const mainForm = this.getMainForm();
      if (!mainForm) return;

      const pdpVariantEl = this.getPdpVariantEl();
      if (pdpVariantEl?.value) {
        const mainVariantInput = mainForm.querySelector('[name="id"]');
        if (mainVariantInput) mainVariantInput.value = pdpVariantEl.value;
      }

      const pdpQtyEl = document.querySelector('.js-pharmacist-pdp-quantity');
      if (pdpQtyEl?.value) {
        const mainQtyInput = mainForm.querySelector('[name="quantity"]');
        if (mainQtyInput) mainQtyInput.value = pdpQtyEl.value;
      }
    }

    /**
     * Handles dose/qty changes on the PDP (outside the drawer).
     * @param {Event} evt
     */
    handlePdpDoseChange(evt) {
      if (evt.target.matches('.js-pharmacist-pdp-variant')) {
        const selectEl = evt.target;
        const doseSection = selectEl.closest('[data-dose-source="mounjaro"]');

        if (doseSection && selectEl.tagName === 'SELECT') {
          const selectedOption = selectEl.options[selectEl.selectedIndex];
          const productUrl = selectedOption?.dataset.url;

          if (productUrl && productUrl !== window.location.pathname) {
            window.location.assign(productUrl);
            return;
          }
        }

        this.syncPdpDoseToMainForm();
        return;
      }

      if (evt.target.matches('.js-pharmacist-pdp-quantity')) {
        this.syncPdpDoseToMainForm();
      }
    }

    /**
     * Opens drawer from PDP add-to-cart trigger.
     * @param {Element} opener - Button that opened the drawer.
     * @param {HTMLFormElement} mainForm - Main product form on PDP.
     */
    openFromProduct(opener, mainForm) {
      this.mainForm = mainForm;
      this.syncPdpDoseToMainForm();
      this.syncFromMainForm();

      super.open(opener, this.form.querySelector('.pharmacist-form__input, .js-pharmacist-variant'));
    }

    /**
     * Sync variant and quantity from the main PDP form into the drawer.
     */
    syncFromMainForm() {
      if (!this.mainForm) return;

      const mainQty = this.mainForm.querySelector('[name="quantity"]')?.value
        || document.querySelector('.js-pharmacist-pdp-quantity')?.value
        || '1';
      const mainVariantId = this.mainForm.querySelector('[name="id"]')?.value
        || this.getPdpVariantEl()?.value;

      const variantEl = this.querySelector('.js-pharmacist-variant');
      if (variantEl && mainVariantId) {
        variantEl.value = mainVariantId;
      }

      const qtyEl = this.form?.querySelector('[name="quantity"]');
      if (qtyEl && mainQty) qtyEl.value = mainQty;
    }

    /**
     * Write drawer variant/qty back to the main PDP form.
     */
    syncToMainForm() {
      if (!this.mainForm) return;

      const variantId = this.getVariantId();
      const qty = this.form?.querySelector('[name="quantity"]')?.value
        || document.querySelector('.js-pharmacist-pdp-quantity')?.value
        || '1';

      const mainVariantInput = this.mainForm.querySelector('[name="id"]');
      if (mainVariantInput && variantId) mainVariantInput.value = variantId;

      const mainQtyInput = this.mainForm.querySelector('[name="quantity"]');
      if (mainQtyInput) mainQtyInput.value = qty;
    }

    /**
     * @returns {string|null}
     */
    getVariantId() {
      const pdpVariantEl = this.getPdpVariantEl();
      if (pdpVariantEl?.value) return pdpVariantEl.value;

      const variantEl = this.querySelector('.js-pharmacist-variant');
      return variantEl ? variantEl.value : null;
    }

    /**
     * @param {Event} evt
     */
    handleDocumentClick(evt) {
      const trigger = evt.target.closest('[data-pharmacist-drawer-trigger]');
      if (!trigger || trigger.disabled || trigger.getAttribute('aria-disabled') === 'true') return;

      const productForm = trigger.closest('product-form');
      const mainForm = productForm?.querySelector('.js-product-form-main');
      if (!mainForm) return;

      evt.preventDefault();
      this.openFromProduct(trigger, mainForm);
    }

    /**
     * @param {Event} evt
     */
    handleChange(evt) {
      if (evt.target.matches('[data-script-type]')) {
        this.toggleScriptPanels(evt.target);
      }

      if (evt.target.matches('.js-pharmacist-variant')) {
        if (this.dataset.doseSource === 'mounjaro' && evt.target.tagName === 'SELECT') {
          const selectedOption = evt.target.options[evt.target.selectedIndex];
          const productUrl = selectedOption?.dataset.url;

          if (productUrl && productUrl !== window.location.pathname) {
            window.location.assign(productUrl);
            return;
          }
        }

        this.updateDoseDetails(evt.target);
        this.syncToMainForm();
      }

      if (evt.target.name === 'quantity') {
        this.syncToMainForm();
      }
    }

    /**
     * Updates price and BC# when a linked dose product is selected.
     * @param {HTMLSelectElement} selectEl
     */
    updateDoseDetails(selectEl) {
      const selectedOption = selectEl.options[selectEl.selectedIndex];
      if (!selectedOption?.value) return;

      const priceEl = this.querySelector('.pharmacist-form-drawer__price-el .price__current');
      if (priceEl && selectedOption.dataset.price) {
        priceEl.textContent = this.formatMoney(selectedOption.dataset.price);
      }

      const bcEl = this.querySelector('.pharmacist-form-drawer__bc');
      if (bcEl && selectedOption.dataset.bc) {
        bcEl.textContent = `BC#: ${selectedOption.dataset.bc}`;
      }
    }

    /**
     * @param {string|number} cents
     * @returns {string}
     */
    formatMoney(cents) {
      const template = theme.settings.moneyWithCurrencyFormat || '${{amount}}';
      const amount = (Number(cents) / 100).toFixed(2);
      return template
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(Number(cents) / 100)))
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    }

    /**
     * @param {HTMLInputElement} selectedRadio
     */
    toggleScriptPanels(selectedRadio) {
      const scriptType = selectedRadio.dataset.scriptType;
      const fieldRoot = selectedRadio.closest('.pharmacist-form__field');
      if (!fieldRoot) return;

      fieldRoot.querySelectorAll('[data-script-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.scriptPanel !== scriptType;
      });
    }

    /**
     * @param {string|false} error
     */
    setError(error = false) {
      if (!this.errorEl) return;
      this.errorEl.hidden = !error;
      this.errorEl.textContent = error || '';
    }

    /**
     * @param {SubmitEvent} evt
     */
    async handleSubmit(evt) {
      evt.preventDefault();

      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      this.setError(false);
      this.submitBtn.classList.add('is-loading');
      this.submitBtn.setAttribute('aria-disabled', 'true');

      this.syncPdpDoseToMainForm();
      this.syncFromMainForm();

      const variantId = this.getVariantId();
      const quantity = parseInt(
        document.querySelector('.js-pharmacist-pdp-quantity')?.value
          || this.form?.querySelector('[name="quantity"]')?.value
          || '1',
        10
      );
      const properties = {};

      const fieldData = new FormData(this.form);
      fieldData.forEach((value, key) => {
        if (key.startsWith('properties[')) {
          const match = key.match(/properties\[(.*?)\]/);
          if (match) properties[match[1]] = value;
        }
      });

      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);
      Object.entries(properties).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, value);
      });
      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', ')).join(', ');
        }

        if (!response.ok || data.status) {
          this.setError(error || 'Unable to add to cart.');
          return;
        }

        this.close(() => {
          const cartIconBubble = document.getElementById('cart-icon-bubble');
          if (cartIconBubble && data.sections?.['cart-icon-bubble']) {
            cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
          }

          if (theme.settings.afterAtc === 'page') {
            window.location.href = theme.routes.cart;
            return;
          }

          if (this.cartDrawer) {
            this.cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
          }
        });

        const itemInOldCart = oldCartData.items.filter(
          (item) => String(item.variant_id) === String(data.variant_id)
        )[0];

        const productForm = document.querySelector(`#${this.productFormId}`)?.closest('product-form');
        if (productForm) {
          if (itemInOldCart) {
            productForm.dispatchEvent(new CustomEvent('on:line-item:change', {
              bubbles: true,
              detail: {
                variantId: data.variant_id,
                oldQuantity: itemInOldCart.quantity,
                newQuantity: itemInOldCart.quantity === data.quantity
                  ? itemInOldCart.quantity : data.quantity
              }
            }));
          } else {
            productForm.dispatchEvent(new CustomEvent('on:cart:add', {
              bubbles: true,
              detail: { variantId: data.variant_id }
            }));
          }
        }
      } catch (err) {
        console.log(err); // eslint-disable-line
        this.setError('Unable to add to cart. Please try again.');
      } finally {
        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.removeAttribute('aria-disabled');
      }
    }
  }

  customElements.define('pharmacist-form-drawer', PharmacistFormDrawer);
}
